// src/components/UserManagement.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  User, Plus, Edit, Trash, Shield, CheckCircle, AlertCircle, 
  Loader, X, Bell, Calendar, Eye, Lock, TrendingUp, Wallet, ChevronDown, ChevronUp
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    fullName: '',
    email: '',
    role: 'user',
    expiryDate: ''
  });
  const [notification, setNotification] = useState({
    message: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  
  // Estados para ver movimientos y cambiar contraseña
  const [showUserMovementsModal, setShowUserMovementsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMovements, setUserMovements] = useState([]);
  const [userStats, setUserStats] = useState({ totalSales: 0, totalIncome: 0 });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    userId: null,
    newPassword: '',
    confirmPassword: ''
  });
  // Filtros para movimientos
  const [userMovementMonthFilter, setUserMovementMonthFilter] = useState('');
  const [userMovementYearFilter, setUserMovementYearFilter] = useState('');
  const [userMovementDayFilter, setUserMovementDayFilter] = useState('');
  const [showMovementDetails, setShowMovementDetails] = useState(false);
  
  const { currentUser, csrfToken } = useAuth();
  
  // Verificar si el usuario es administrador
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      setError('No tienes permisos para acceder a esta sección.');
      setLoading(false);
      return;
    }
    
    fetchUsers();
  }, [currentUser]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los usuarios. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  
  // Función mejorada para obtener movimientos con filtros pasados directamente
  const fetchUserMovementsWithFilters = async (userId, month, year, day) => {
    try {
      setLoading(true);
      
      // Construir URL con parámetros de filtro
      let url = `/api/movements/user/${userId}`;
      const params = new URLSearchParams();
      
      // Si hay día seleccionado, necesitamos mes y año también
      if (day && month && year) {
        params.append('day', day);
        params.append('month', month);
        params.append('year', year);
      } else {
        // Si no hay día, usamos los filtros normales de mes y año
        if (month) {
          params.append('month', month);
        }
        if (year) {
          params.append('year', year);
        }
      }
      
      // Añadir parámetros a la URL si hay alguno
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setUserMovements(response.data);
      
      // Calcular estadísticas
      const stats = response.data.reduce((acc, movement) => {
        if (movement.type === 'venta') {
          acc.totalSales++;
          acc.totalIncome += parseFloat(movement.amount);
        }
        return acc;
      }, { totalSales: 0, totalIncome: 0 });
      
      setUserStats(stats);
      
      // Si hay resultados, mostrar automáticamente los detalles
      if (response.data.length > 0) {
        setShowMovementDetails(true);
      }
    } catch (err) {
      console.error('Error al obtener movimientos:', err);
      setError('Error al obtener movimientos del usuario');
    } finally {
      setLoading(false);
    }
  };
  
  // Función original modificada para usar la nueva función con filtros
  const fetchUserMovements = async (userId) => {
    fetchUserMovementsWithFilters(userId, userMovementMonthFilter, userMovementYearFilter, userMovementDayFilter);
  };
  
  // Función para abrir modal de movimientos
  const handleViewUserMovements = async (user) => {
    setSelectedUser(user);
    setShowUserMovementsModal(true);
    setShowMovementDetails(false);
    // Resetear los filtros
    setUserMovementMonthFilter('');
    setUserMovementYearFilter('');
    setUserMovementDayFilter('');
    await fetchUserMovementsWithFilters(user.id, '', '', '');
  };
  
  // Función para cambiar contraseña del usuario
  const handleChangeUserPassword = async () => {
    try {
      setLoading(true);
      setFormError('');
      
      // Validaciones
      if (!changePasswordData.newPassword || !changePasswordData.confirmPassword) {
        setFormError('Por favor, completa todos los campos');
        setLoading(false);
        return;
      }
      
      if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
        setFormError('Las contraseñas no coinciden');
        setLoading(false);
        return;
      }
      
      if (changePasswordData.newPassword.length < 6) {
        setFormError('La contraseña debe tener al menos 6 caracteres');
        setLoading(false);
        return;
      }
      
      await axios.post(`/api/auth/admin/change-password/${changePasswordData.userId}`, {
        newPassword: changePasswordData.newPassword,
        _csrf: csrfToken
      });
      
      setFormSuccess('Contraseña actualizada correctamente');
      
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setChangePasswordData({ userId: null, newPassword: '', confirmPassword: '' });
        setFormSuccess('');
      }, 2000);
      
    } catch (err) {
      console.error('Error:', err);
      setFormError(err.response?.data?.error || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para abrir modal de cambio de contraseña
  const handleOpenChangePassword = (user) => {
    setChangePasswordData({
      userId: user.id,
      newPassword: '',
      confirmPassword: ''
    });
    setSelectedUser(user);
    setShowChangePasswordModal(true);
  };
  
  const handleSaveUser = async () => {
    try {
      setLoading(true);
      setFormError('');
      setFormSuccess('');
      
      // Validaciones
      if (!newUser.username || (!editMode && !newUser.password) || !newUser.fullName || !newUser.email) {
        setFormError('Por favor, completa todos los campos obligatorios');
        setLoading(false);
        return;
      }
      
      if (!editMode && newUser.password.length < 6) {
        setFormError('La contraseña debe tener al menos 6 caracteres');
        setLoading(false);
        return;
      }
      
      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newUser.email)) {
        setFormError('Formato de email inválido');
        setLoading(false);
        return;
      }
      
      const userData = {
        ...newUser,
        _csrf: csrfToken
      };
      
      if (editMode) {
        // Actualizar usuario existente
        await axios.put(`/api/users/${newUser.id}`, userData);
      } else {
        // Crear nuevo usuario
        await axios.post('/api/users', userData);
      }
      
      // Recargar la lista de usuarios
      await fetchUsers();
      
      // Mostrar mensaje de éxito
      setFormSuccess(editMode ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente');
      
      // Limpiar formulario después de 2 segundos
      setTimeout(() => {
        setNewUser({ username: '', password: '', fullName: '', email: '', role: 'user', expiryDate: '' });
        setShowUserForm(false);
        setEditMode(false);
        setFormSuccess('');
      }, 2000);
      
    } catch (err) {
      console.error('Error:', err);
      setFormError(err.response?.data?.error || 'Error al guardar el usuario');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendNotification = async () => {
    try {
      setLoading(true);
      setFormError('');
      setFormSuccess('');
      
      // Validación del mensaje
      if (!notification.message || notification.message.trim() === '') {
        setFormError('Por favor, ingresa un mensaje para la notificación');
        setLoading(false);
        return;
      }
      
      // Enviar notificación a todos los usuarios
      await axios.post('/api/notifications/all', {
        message: notification.message,
        _csrf: csrfToken
      });
      
      // Mostrar mensaje de éxito
      setFormSuccess('Notificación enviada correctamente a todos los usuarios');
      
      // Limpiar formulario después de 2 segundos
      setTimeout(() => {
        setNotification({ message: '' });
        setShowNotificationForm(false);
        setFormSuccess('');
      }, 2000);
      
    } catch (err) {
      console.error('Error:', err);
      setFormError(err.response?.data?.error || 'Error al enviar la notificación');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditUser = (user) => {
    setNewUser({
      id: user.id,
      username: user.username,
      password: '', // No mostrar la contraseña actual
      fullName: user.full_name,
      email: user.email,
      role: user.role,
      expiryDate: user.expiry_date || '' // Asignar la fecha de expiración si existe
    });
    setEditMode(true);
    setShowUserForm(true);
  };
  
  const handleDeleteUser = async (userId) => {
    // No permitir eliminar al usuario actual
    if (userId === currentUser.id) {
      alert('No puedes eliminar tu propia cuenta');
      return;
    }
    
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        setLoading(true);
        await axios.delete(`/api/users/${userId}`, { 
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
        await fetchUsers();
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el usuario');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Verificar si un usuario ha expirado
  const isUserExpired = (user) => {
    if (!user.expiry_date) return false;
    
    const expiryDate = new Date(user.expiry_date);
    const today = new Date();
    
    return expiryDate < today;
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Mostrar indicador de carga si es necesario
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-gray-400">Cargando usuarios...</p>
        </div>
      </div>
    );
  }
  
  // Mostrar mensaje de error si hay uno
  if (error && users.length === 0) {
    return (
      <div className="p-6 bg-red-900 bg-opacity-25 rounded-lg">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          <Shield size={24} className="text-purple-400 mr-2" />
          Gestión de Usuarios
        </h1>
        <div className="flex space-x-2">
          <button 
            className="bg-purple-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
            onClick={() => setShowNotificationForm(true)}
          >
            <Bell size={20} /> Enviar Notificación
          </button>
          <button 
            className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
            onClick={() => {
              setNewUser({ username: '', password: '', fullName: '', email: '', role: 'user', expiryDate: '' });
              setEditMode(false);
              setShowUserForm(true);
            }}
          >
            <Plus size={20} /> Nuevo Usuario
          </button>
        </div>
      </div>
      
      {formSuccess && (
        <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
          <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
          <p className="text-sm text-green-500">{formSuccess}</p>
        </div>
      )}
      
      {users.length === 0 ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <p className="text-gray-400">No hay usuarios registrados además de tu cuenta de administrador.</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 rounded-md text-white"
            onClick={() => {
              setNewUser({ username: '', password: '', fullName: '', email: '', role: 'user', expiryDate: '' });
              setEditMode(false);
              setShowUserForm(true);
            }}
          >
            Crear el primer usuario
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Nombre Completo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Último Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Expiración</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {users.map((user, index) => (
                <tr key={user.id} className={`${user.id === currentUser.id ? "bg-gray-700 bg-opacity-40" : ""} ${isUserExpired(user) ? "bg-red-900 bg-opacity-20" : ""}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                        <User size={16} className="text-gray-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-white">{user.username}</p>
                        {user.id === currentUser.id && (
                          <span className="text-xs text-purple-400">(Tú)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{user.full_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300'
                    }`}>
                      {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(user.last_login)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.expiry_date ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isUserExpired(user) ? 'bg-red-900 text-red-300' : 'bg-green-900 text-green-300'
                      }`}>
                        {formatDate(user.expiry_date)}
                      </span>
                    ) : (
                      <span className="text-gray-500">Sin expiración</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <div className="flex items-center space-x-2">
                      <button 
                        className="text-purple-400 hover:text-purple-300 p-1 rounded hover:bg-gray-700 transition-colors"
                        onClick={() => handleViewUserMovements(user)}
                        title="Ver movimientos"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-gray-700 transition-colors"
                        onClick={() => handleOpenChangePassword(user)}
                        title="Cambiar contraseña"
                      >
                        <Lock size={16} />
                      </button>
                      <button 
                        className="text-blue-400 hover:text-blue-300 p-1 rounded hover:bg-gray-700 transition-colors"
                        onClick={() => handleEditUser(user)}
                        title="Editar usuario"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-gray-700 transition-colors"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.id === currentUser.id}
                        title={user.id === currentUser.id ? "No puedes eliminarte a ti mismo" : "Eliminar usuario"}
                      >
                        <Trash size={16} className={user.id === currentUser.id ? "text-gray-500 cursor-not-allowed" : ""} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Modal para crear/editar usuario */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editMode ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  setShowUserForm(false);
                  setFormError('');
                  setFormSuccess('');
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {formError && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{formError}</p>
              </div>
            )}
            
            {formSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{formSuccess}</p>
              </div>
            )}
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre de Usuario *</label>
                <input 
                  type="text" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                />
              </div>
              
              {!editMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Contraseña *</label>
                  <input 
                    type="password" 
                    className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                  <p className="text-xs text-gray-400 mt-1">Mínimo 6 caracteres</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre Completo *</label>
                <input 
                  type="text" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                <input 
                  type="email" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Rol</label>
                <select 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Los administradores tienen acceso a todas las funciones, incluida la gestión de usuarios.
                </p>
              </div>
              
              <div>
                <label className="flex items-center text-sm font-medium text-gray-300 mb-1">
                  <Calendar size={16} className="mr-2 text-blue-400" />
                  Fecha de Expiración
                </label>
                <input 
                  type="date" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newUser.expiryDate}
                  onChange={(e) => setNewUser({ ...newUser, expiryDate: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Opcional. Si se establece, el usuario no podrá acceder después de esta fecha.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                  onClick={() => {
                    setShowUserForm(false);
                    setFormError('');
                    setFormSuccess('');
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-blue-600 rounded-md text-white"
                  onClick={handleSaveUser}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <Loader size={16} className="animate-spin mr-2" />
                      Guardando...
                    </span>
                  ) : (
                    "Guardar"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal para enviar notificación */}
      {showNotificationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Bell size={20} className="text-purple-400 mr-2" />
                Enviar Notificación a Todos los Usuarios
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  setShowNotificationForm(false);
                  setFormError('');
                  setFormSuccess('');
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {formError && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{formError}</p>
              </div>
            )}
            
            {formSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{formSuccess}</p>
              </div>
            )}
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Mensaje *</label>
                <textarea 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white" 
                  rows="4"
                  placeholder="Escribe aquí el mensaje para todos los usuarios..."
                  value={notification.message}
                  onChange={(e) => setNotification({ ...notification, message: e.target.value })}
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                  onClick={() => {
                    setShowNotificationForm(false);
                    setFormError('');
                    setFormSuccess('');
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-purple-600 rounded-md text-white"
                  onClick={handleSendNotification}
                  disabled={loading || !notification.message.trim()}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <Loader size={16} className="animate-spin mr-2" />
                      Enviando...
                    </span>
                  ) : (
                    "Enviar Notificación"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Modal para ver movimientos del usuario */}
      {showUserMovementsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <TrendingUp size={20} className="text-blue-400 mr-2" />
                Estadísticas de {selectedUser.full_name}
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  setShowUserMovementsModal(false);
                  setUserMovementMonthFilter('');
                  setUserMovementYearFilter('');
                  setUserMovementDayFilter('');
                  setShowMovementDetails(false);
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Filtros por día, mes y año */}
            <div className="mb-4 grid grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Día</label>
                <select 
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-md"
                  value={userMovementDayFilter}
                  onChange={(e) => {
                    const newDay = e.target.value;
                    setUserMovementDayFilter(newDay);
                    fetchUserMovementsWithFilters(selectedUser.id, userMovementMonthFilter, userMovementYearFilter, newDay);
                  }}
                  disabled={!userMovementMonthFilter || !userMovementYearFilter}
                >
                  <option value="">Todos</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Mes</label>
                <select 
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-md"
                  value={userMovementMonthFilter}
                  onChange={(e) => {
                    const newMonth = e.target.value;
                    setUserMovementMonthFilter(newMonth);
                    // Reset día cuando cambia el mes
                    setUserMovementDayFilter('');
                    fetchUserMovementsWithFilters(selectedUser.id, newMonth, userMovementYearFilter, '');
                  }}
                >
                  <option value="">Todos</option>
                  <option value="1">Enero</option>
                  <option value="2">Febrero</option>
                  <option value="3">Marzo</option>
                  <option value="4">Abril</option>
                  <option value="5">Mayo</option>
                  <option value="6">Junio</option>
                  <option value="7">Julio</option>
                  <option value="8">Agosto</option>
                  <option value="9">Septiembre</option>
                  <option value="10">Octubre</option>
                  <option value="11">Noviembre</option>
                  <option value="12">Diciembre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Año</label>
                <select 
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-md"
                  value={userMovementYearFilter}
                  onChange={(e) => {
                    const newYear = e.target.value;
                    setUserMovementYearFilter(newYear);
                    // Reset día cuando cambia el año
                    setUserMovementDayFilter('');
                    fetchUserMovementsWithFilters(selectedUser.id, userMovementMonthFilter, newYear, '');
                  }}
                >
                  <option value="">Todos</option>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Estadísticas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-900 bg-opacity-30 p-4 rounded-lg">
                <div className="flex items-center">
                  <Wallet size={24} className="text-green-400 mr-2" />
                  <div>
                    <p className="text-xs text-gray-400">Total Ventas</p>
                    <p className="text-xl font-bold text-green-400">{userStats.totalSales}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-900 bg-opacity-30 p-4 rounded-lg">
                <div className="flex items-center">
                  <TrendingUp size={24} className="text-blue-400 mr-2" />
                  <div>
                    <p className="text-xs text-gray-400">Ingreso Total</p>
                    <p className="text-xl font-bold text-blue-400">{formatCurrency(userStats.totalIncome)}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Separador y botón para mostrar/ocultar detalles */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Detalle de Movimientos</h3>
              <button 
                className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300 hover:bg-gray-600 flex items-center"
                onClick={() => setShowMovementDetails(!showMovementDetails)}
              >
                {showMovementDetails ? (
                  <>Ocultar Detalles <ChevronUp size={14} className="ml-1" /></>
                ) : (
                  <>Mostrar Detalles <ChevronDown size={14} className="ml-1" /></>
                )}
              </button>
            </div>
            
            {/* Lista de movimientos (colapsable) */}
            {showMovementDetails && (
              <div className="mt-2 border border-gray-700 rounded-md max-h-60 overflow-y-auto">
                {userMovements.length > 0 ? (
                  <div className="divide-y divide-gray-700">
                    {userMovements.map((movement) => (
                      <div key={movement.id} className="p-2 hover:bg-gray-700">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-300">{new Date(movement.date).toLocaleDateString()}</span>
                          <span className={`text-xs ${movement.type === 'venta' ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(movement.amount)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{movement.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-400">No hay movimientos en este período</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <button 
                type="button" 
                className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                onClick={() => {
                  setShowUserMovementsModal(false);
                  setUserMovementMonthFilter('');
                  setUserMovementYearFilter('');
                  setUserMovementDayFilter('');
                  setShowMovementDetails(false);
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para cambiar contraseña de usuario */}
      {showChangePasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Lock size={20} className="text-yellow-400 mr-2" />
                Cambiar Contraseña - {selectedUser.full_name}
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setFormError('');
                  setFormSuccess('');
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {formError && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{formError}</p>
              </div>
            )}
            
            {formSuccess && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{formSuccess}</p>
              </div>
            )}
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nueva Contraseña</label>
                <input 
                  type="password" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={changePasswordData.newPassword}
                  onChange={(e) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">Mínimo 6 caracteres</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Confirmar Contraseña</label>
                <input 
                  type="password" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={changePasswordData.confirmPassword}
                  onChange={(e) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setFormError('');
                    setFormSuccess('');
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-yellow-600 rounded-md text-white"
                  onClick={handleChangeUserPassword}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <Loader size={16} className="animate-spin mr-2" />
                      Guardando...
                    </span>
                  ) : (
                    "Cambiar Contraseña"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
